import React, { useState, useEffect } from "react";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import { Divider, Icon } from "@amzn/meridian";
import clipboardTokens from "@amzn/meridian-tokens/base/icon/clipboard";
import checkCircleTokens from "@amzn/meridian-tokens/base/icon/check-circle";
import chevronRightSmallTokens from "@amzn/meridian-tokens/base/icon/chevron-right-small";
import { useNavigate } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "src/store/store";
import { getInspectionsByUser } from "src/store/inspectionSlice";
import { Inspection } from "src/data/types";
import { formatShortDate } from "src/utils/dateUtil";
import "./landing.scss";

const HistoryView: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { incompleteInspections, completedInspections } = useAppSelector((state) => state.inspection);
  const [showIncomplete, setShowIncomplete] = useState(false);

  // Only re-fetch when inspections have changed (e.g. new inspection created)
  useEffect(() => {
      dispatch(getInspectionsByUser());
  }, []);

  const handleIncompleteClick = (inspection: Inspection) => {
    if (inspection.templateId.startsWith("WorkflowDriven")) {
      navigate(`/configurationDrivenInspections/${inspection.inspectionId}/review`);
    } else {
      navigate(`/inspections/${inspection.inspectionId}`);
    }
  };

  return (
    <Column spacing="400" spacingInset="none 500 500">
      <Text type="h300">History</Text>

      {/* Incomplete Inspections card */}
      <div onClick={() => setShowIncomplete(!showIncomplete)}>
        <Row className="home-card" alignmentVertical="center" widths={["fit", "fill", "fit"]} spacing="400" spacingInset="400">
          <Row spacingInset="300" className="home-workflow-icon-bg">
            <Icon tokens={clipboardTokens} color="success" />
          </Row>
          <Column spacing="none">
            <Text type="b300">Incomplete Inspections</Text>
            <Text type="b100" color="secondary">{incompleteInspections.length} in progress</Text>
          </Column>
          <Icon tokens={chevronRightSmallTokens} />
        </Row>
      </div>

      {/* Expanded incomplete list */}
      {showIncomplete && incompleteInspections.length > 0 && (
        <Column className="home-card" spacing="200">
          {incompleteInspections.map((inspection, idx) => (
            <div key={inspection.inspectionId} onClick={() => handleIncompleteClick(inspection)}>
              {idx > 0 && <Divider />}
              <Row alignmentVertical="center" widths={["fit", "fill", "fit"]} spacing="400" spacingInset="400">
                <Row spacingInset="300" className="home-workflow-icon-bg">
                  <Icon tokens={clipboardTokens} color="success" />
                </Row>
                <Column spacing="none" width="fill">
                  <Text type="b300">{inspection.templateName}</Text>
                  <Text type="b100" color="secondary">
                    {inspection.siteCode} · {inspection.complianceStats.questionCount} checks
                  </Text>
                </Column>
                <Column alignmentHorizontal="end" spacing="none">
                  <Icon tokens={chevronRightSmallTokens} />
                  <Text type="b100" color="secondary">{formatShortDate(inspection.createdAt)}</Text>
                </Column>
              </Row>
            </div>
          ))}
        </Column>
      )}

      {showIncomplete && incompleteInspections.length === 0 && (
        <Column className="home-card" spacingInset="500" alignmentHorizontal="center">
          <Text color="secondary">No incomplete inspections</Text>
        </Column>
      )}

      {/* Completed Inspections card */}
      <div onClick={() => navigate("/completedInspections")}>
        <Row className="home-card" alignmentVertical="center" widths={["fit", "fill", "fit"]} spacing="400" spacingInset="400">
          <Row spacingInset="300" className="home-workflow-icon-bg">
            <Icon tokens={checkCircleTokens} color="success" />
          </Row>
          <Column spacing="none">
            <Text type="b300">Completed Inspections</Text>
            <Text type="b100" color="secondary">{completedInspections.length} completed</Text>
          </Column>
          <Icon tokens={chevronRightSmallTokens} />
        </Row>
      </div>
    </Column>
  );
};

export default HistoryView;
